import React, { useEffect, useState } from 'react'
import { Input, Pagination, Button, Dropdown } from '@/components/atoms/index'
import { vars } from '@/style/theme.css'
import { Icon } from '@/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import * as styles from '@/components/organisms/Table/Table.css'
import { AcademyWordTabItems } from '@/pages/academy/word/list'
import { academyWordExamApi } from '@/services/api'
import { useSessionSlug } from '@/hooks/useSessionSlug'

interface WordExamData {
  id: string
  examName: string
  examType: string
  registrant: string
  createdAt: string
  isPublic: boolean
}

export default function AcademyWordbookList() {
  const navigate = useNavigate()
  const location = useLocation()
  const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug()
  const academyCode = sessionAcademyCode || ''
  const [searchText, setSearchText] = useState('')
  const [isPublicFilter, setIsPublicFilter] = useState<string>('all') // all, public, private
  const [rows, setRows] = useState<WordExamData[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 10
  const [total, setTotal] = useState(0)

  const publicFilterOptions = [
    { label: '전체', value: 'all' },
    { label: '공개', value: 'public' },
    { label: '비공개', value: 'private' }
  ]

  const formatCreatedAt = (value: any) => {
    if (!value) return ''
    const str = value.toString()
    if (str.includes('T')) {
      return str.split('T')[0]
    }
    return str.substring(0, 10)
  }

  const renderPublicStatus = (isPublic: boolean) => {
    const baseClasses = 'px-2 py-1 text-xs rounded-full'
    return isPublic ? (
      <span className={`${baseClasses} text-green-800 bg-green-100`}>공개</span>
    ) : (
      <span className={`${baseClasses} text-gray-800 bg-gray-100`}>비공개</span>
    )
  }

  const fetchList = async (p = page, overrideQ?: string, overrideIsPublic?: string) => {
    if (!academyCode) return
    const q = typeof overrideQ === 'string' ? overrideQ : searchText
    const publicFilter = typeof overrideIsPublic === 'string' ? overrideIsPublic : isPublicFilter
    
    const params: any = { q, page: p - 1, size: pageSize, academyCode }
    if (publicFilter === 'public') {
      params.isPublic = true
    } else if (publicFilter === 'private') {
      params.isPublic = false
    }
    
    const res = await academyWordExamApi.list(params)
    const data = res?.data || res
    const content = data?.content || []
    const totalElements = data?.totalElements || content.length
    const mapped = content.map((it:any)=>({
      id: String(it.id),
      examName: it.name || it.title || '',
      examType: it.examType === 'word' ? '단어 작성형' : it.examType === 'meaning' ? '해석 작성형' : it.examType || '',
      registrant: it.registrant || '',
      createdAt: formatCreatedAt(it.createdAt),
      isPublic: !!it.isPublic
    }))
    const tokens = (q || '').toLowerCase().split(/\s+/).filter(Boolean)
    const filteredList = tokens.length
      ? mapped.filter((item: WordExamData) => {
          const name = (item.examName || '').toLowerCase()
          const type = (item.examType || '').toLowerCase()
          const registrant = (item.registrant || '').toLowerCase()
          return tokens.every(token => name.includes(token) || type.includes(token) || registrant.includes(token))
        })
      : mapped // [QA Fix] 검색어를 시험명/유형/등록자에 모두 AND 매칭
    setRows(filteredList)
    setPage(p)
    setTotal(tokens.length ? filteredList.length : totalElements)
  }

  useEffect(()=>{
    if (sessionStatus !== 'ready' || !academyCode) return;
    const params = new URLSearchParams(location.search)
    const p = Number(params.get('page') || '1')
    const q = params.get('q') || ''
    const publicFilter = params.get('isPublic') || 'all'
    if (searchText !== q) setSearchText(q)
    if (isPublicFilter !== publicFilter) setIsPublicFilter(publicFilter)
    fetchList(p, q, publicFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, academyCode, sessionStatus])

  const handleRowClick = (wordExamId: string) => {
    navigate(`/academy/word/word_exam/detail/${wordExamId}`)
  }

  const handleSearchNavigate = (targetPage: number, query = searchText, visibility = isPublicFilter) => {
    navigate(`/academy/word/word_exam/list?page=${targetPage}&q=${encodeURIComponent(query)}&isPublic=${visibility}`)
  }

  return (
    <main className='flex flex-col flex-1 pt-8'>
      <div className='flex flex-row justify-between items-start'>
        <h1 className='heading1'>단어 관리</h1>
        <Button intent="primary" onClick={() => navigate('/academy/word/word_exam/detail')}>
          단어시험 추가
        </Button>
      </div>

      <AcademyWordTabItems activeTab='exam' setActiveTab={() => {}} />
      <main className='flex flex-col flex-1 w-full'>
        <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-start lg:items-center'>
          <Input
            placeholder='검색어를 입력해주세요.'
            className='max-w-[500px]'
            icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
            value={searchText}
            onChange={(e:any)=>setSearchText(e?.target?.value || '')}
            onKeyDown={(e:any)=>{ if (e.key==='Enter') handleSearchNavigate(1) }}
          />
          <Dropdown
            placeholder='공개 범위'
            className='max-w-[200px]'
            options={publicFilterOptions}
            value={isPublicFilter}
            onChange={(value)=>{
              const nextValue = value || 'all'
              setIsPublicFilter(nextValue)
              handleSearchNavigate(1, searchText, nextValue)
            }}
          />
        </section>

        <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
            <div>
            {/* 테이블 헤더 */}
            <ul className={styles.academyHeaderList}>
                <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                <li className={`${styles.headerItem} w-full`}>단어시험명</li>
                <li className={`${styles.headerItem} w-full`}>시험유형</li>
                <li className={`${styles.headerItem} w-full`}>공개 여부</li>
                <li className={`${styles.headerItem} w-full`}>등록자</li>
                <li className={`${styles.headerItem} w-full`}>등록일</li>
            </ul>

            {/* 테이블 바디 */}
            <div>
                {rows.map((wordExam, index) => (
                <div 
                    key={wordExam.id}
                    className={`${styles.mobileCard} ${styles.hoverRow} cursor-pointer`}
                    onClick={() => handleRowClick(wordExam.id)}
                >
                    <ul className={styles.academyBodyList}>
                    <li className={`${styles.academyBodyItem} max-w-[80px] justify-center`}>
                        {total - ((page - 1) * pageSize + index)}
                    </li>
                    <li className={styles.academyBodyItem}>
                        <div className={styles.wordText}>
                        {wordExam.examName}
                        </div>
                    </li>
                    <li className={styles.academyBodyItem}>
                        {wordExam.examType}
                    </li>
                    <li className={styles.academyBodyItem}>
                        {renderPublicStatus(wordExam.isPublic)}
                    </li>
                    <li className={styles.academyBodyItem}>
                        {wordExam.registrant}
                    </li>
                    <li className={styles.academyBodyItem}>
                        {wordExam.createdAt}
                    </li>
                    </ul>
                </div>
                ))}
            </div>

            {/* 데이터가 없을 때 */}
            {rows.length === 0 && (
                <div className='py-12 text-center text-gray-500'>
                등록된 단어시험이 없습니다.
                </div>
            )}

            </div>
            <div className='py-5'>
                <Pagination
                  total={total}
                  pageSize={pageSize}
                  current={page}
                  onChange={(p)=>handleSearchNavigate(p)}
                />
                </div>
        </section>
      </main>
    </main>
  )
}